"use client";

import React from "react";
import { BedDouble, Eye, ConciergeBell, Bed, Coffee, Wifi, BookOpen, Wind, Crown, Users, Car } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const rooms = [{
  id: 1,
  title: "Post-Hike Recovery",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639921/img/post-hike-recovery.png"
}, {
  id: 2,
  title: "Senior Dog Comfort",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639921/img/senior-comfort.png"
}, {
  id: 3,
  title: "Performance Support",
  image: "https://durangodogmassage.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-19-at-21.08.49-1.jpeg?v=mrtjjgddbrs"
}, {
  id: 4,
  title: "Mobility & Movement",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639921/img/mobility-support.png"
}, {
  id: 5,
  title: "Relaxation & Calm",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639921/img/relaxation.png"
}];
const roomAmenities = [{
  roomId: 1,
  iconName: "Wind",
  text: "Lactic Acid Flush"
}, {
  roomId: 1,
  iconName: "BedDouble",
  text: "Muscle Repair"
}, {
  roomId: 1,
  iconName: "Users",
  text: "Joint Mobility"
}, {
  roomId: 2,
  iconName: "Bed",
  text: "Arthritis Relief"
}, {
  roomId: 2,
  iconName: "Eye",
  text: "Gentle Touch"
}, {
  roomId: 2,
  iconName: "Crown",
  text: "Quality of Life"
}, {
  roomId: 3,
  iconName: "Wind",
  text: "Agility Prep"
}, {
  roomId: 3,
  iconName: "Crown",
  text: "Elite Conditioning"
}, {
  roomId: 3,
  iconName: "Users",
  text: "Injury Prevention"
}, {
  roomId: 4,
  iconName: "Car",
  text: "In-Home Therapy"
}, {
  roomId: 4,
  iconName: "Bed",
  text: "Range of Motion"
}, {
  roomId: 4,
  iconName: "Eye",
  text: "Gait Analysis"
}, {
  roomId: 5,
  iconName: "Coffee",
  text: "Anxiety Reduction"
}, {
  roomId: 5,
  iconName: "Wind",
  text: "Nervous System"
}, {
  roomId: 5,
  iconName: "BedDouble",
  text: "Deep Rest"
}];
const getIcon = (name: string) => {
  const commonClasses = "size-5 text-chart-2 shrink-0";
  switch (name) {
    case "BedDouble":
      return <BedDouble className={commonClasses} />;
    case "Eye":
      return <Eye className={commonClasses} />;
    case "ConciergeBell":
      return <ConciergeBell className={commonClasses} />;
    case "Bed":
      return <Bed className={commonClasses} />;
    case "Coffee":
      return <Coffee className={commonClasses} />;
    case "Wifi":
      return <Wifi className={commonClasses} />;
    case "BookOpen":
      return <BookOpen className={commonClasses} />;
    case "Wind":
      return <Wind className={commonClasses} />;
    case "Crown":
      return <Crown className={commonClasses} />;
    case "Users":
      return <Users className={commonClasses} />;
    case "Car":
      return <Car className={commonClasses} />;
    default:
      return null;
  }
};
export default function SanctuariesOfPeace() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224804" id="recovery-paths-for-every-dog" className="py-24 bg-background overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-8 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6">
            Recovery Paths for Every Dog
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            From high-alpine athletes to senior companions, our clinical bodywork is tailored to support every stage of your dog's active life in Durango.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full relative">
          <CarouselContent className="-ml-4 md:-ml-6">
            {rooms.map((room, i) => <CarouselItem key={room.id} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                <div className="group relative overflow-hidden transition-all duration-700 hover:-translate-y-1 cursor-pointer h-full rounded-lg shadow-sm">
                  <AspectRatio ratio={4 / 5} className="h-full">
                    <img src={room.image} alt={room.title} className="object-cover w-full h-full" />
                    
                    {}
                    <div className="absolute inset-0 bg-primary/70 transition-colors duration-700 group-hover:bg-primary/90 mix-blend-multiply" />
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-transparent to-transparent opacity-50" />

                    {}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10 border-l-0 group-hover:border-l-4 border-chart-2 transition-all duration-500">
                      
                      {}
                      <h3 className="text-2xl md:text-2xl font-default text-white font-semibold tracking-wide transition-transform duration-500 ease-out group-hover:-translate-y-10">
                        {room.title}
                      </h3>

                      {}
                      <div className="absolute top-1/2 left-0 w-full pt-2 opacity-0 translate-y-8 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0 flex flex-col items-center gap-4 pointer-events-none">
                        {roomAmenities.filter(a => a.roomId === room.id).map((amenity, j) => <div key={j} data-index={j} className="flex items-center gap-3 text-white/95">
                              {getIcon(amenity.iconName)}
                              <span className="text-xs tracking-widest uppercase font-medium font-default">
                                {amenity.text}
                              </span>
                            </div>)}
                      </div>
                      
                    </div>
                  </AspectRatio>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="hidden md:block">
            <CarouselPrevious className="absolute -left-5 lg:-left-12 top-1/2 -translate-y-1/2 size-14 rounded-sm border border-primary text-primary bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-colors duration-300 shadow-md" />
            <CarouselNext className="absolute -right-5 lg:-right-12 top-1/2 -translate-y-1/2 size-14 rounded-sm border border-primary text-primary bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-colors duration-300 shadow-md" />
          </div>
        </Carousel>
      </div>
    </section>;
}